import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  isSameDay, 
  isToday,
  parseISO,
  getHours,
  getMinutes
} from 'date-fns';

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '100px repeat(7, 1fr)',
  gap: 0,
  padding: 0,
  minHeight: '1200px',
  minWidth: '800px'
}));

const TimeSlot = styled(Box)(({ theme }) => ({
  height: 50,
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  fontWeight: 500
}));

const DayColumn = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02)
  }
}));

const DayHeader = styled(Box)(({ theme, istoday }) => ({
  padding: theme.spacing(1.5),
  textAlign: 'center',
  borderBottom: `2px solid ${istoday ? theme.palette.primary.main : alpha(theme.palette.grey[300], 0.5)}`,
  backgroundColor: istoday ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backdropFilter: 'blur(10px)'
}));

const ShiftCard = styled(Paper)(({ theme, shiftcolor }) => ({
  position: 'absolute',
  left: 4,
  right: 4,
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: shiftcolor || theme.palette.primary.main,
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 500,
  zIndex: 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(shiftcolor || theme.palette.primary.main, 0.4)}`
  }
}));

const EmptySlot = styled(Box)(({ theme }) => ({
  height: 50,
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 1,
    backgroundColor: alpha(theme.palette.primary.main, 0.05)
  }
}));

// Color palette for different departments/shift types
const shiftColors = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#00796b',
  '#5d4037', '#616161', '#0097a7', '#fbc02d', '#e64a19', '#512da8'
];

const ScheduleCalendar = ({ 
  currentDate, 
  viewMode, 
  employees, 
  tasks,
  shifts, // Keep for backward compatibility
  onTaskClick,
  onShiftClick, // Keep for backward compatibility
  onTaskCreate,
  onShiftCreate, // Keep for backward compatibility
  onTaskDelete,
  onShiftDelete, // Keep for backward compatibility
  timeFormat = '12hour',
  onLoadTemplate,
  canEdit = true
}) => {
  // Use tasks if provided, otherwise fall back to shifts for compatibility
  const currentTasks = tasks || shifts;
  const handleTaskClick = onTaskClick || onShiftClick;
  const handleTaskCreate = onTaskCreate || onShiftCreate;
  const handleTaskDelete = onTaskDelete || onShiftDelete;
  const theme = useTheme();
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  
  // Day shifts popover state
  const [dayShiftsOpen, setDayShiftsOpen] = useState(false);
  const [dayShiftsAnchor, setDayShiftsAnchor] = useState(null);
  const [selectedDayShifts, setSelectedDayShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Generate time slots (all 24 hours)
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  // Get week days
  const weekStart = startOfWeek(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Get color for department
  const getDepartmentColor = (department) => {
    if (department?.color) {
      return department.color;
    }
    // Default blue if no department or color
    return '#1976d2';
  };

  // Calculate shift position and height
  const getShiftStyle = (shift) => {
    const startTime = parseISO(`${shift.shift_date}T${shift.start_time}`);
    const endTime = parseISO(`${shift.shift_date}T${shift.end_time}`);
    
    const startHour = getHours(startTime);
    const startMinute = getMinutes(startTime);
    const endHour = getHours(endTime);
    const endMinute = getMinutes(endTime);
    
    const startSlot = startHour;
    const duration = (endHour - startHour) + (endMinute - startMinute) / 60;
    
    const top = (startSlot * 50) + (startMinute / 60 * 50) + 4;
    const height = Math.max(duration * 50 - 8, 42);
    
    return {
      top: `${top}px`,
      height: `${height}px`
    };
  };

  // Handle context menu
  const handleContextMenu = (event, task) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY });
    setSelectedTask(task);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setSelectedTask(null);
  };

  const handleDayShiftsClose = () => {
    setDayShiftsOpen(false);
    setDayShiftsAnchor(null);
    setSelectedDayShifts([]);
  };

  // Handle task actions
  const handleEditTask = () => {
    if (selectedTask) {
      handleTaskClick(selectedTask);
    }
    handleContextMenuClose();
  };

  const handleDeleteTask = () => {
    if (selectedTask) {
      handleTaskDelete(selectedTask.id);
    }
    handleContextMenuClose();
  };

  // Handle empty slot click
  const handleSlotClick = (day, hour) => {
    if (!canEdit || !handleTaskCreate) return;
    
    const clickDate = new Date(day);
    clickDate.setHours(hour, 0, 0, 0);
    handleTaskCreate(clickDate);
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, day, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot(`${day.toString()}-${hour}`);
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = (e, day, hour) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    if (draggedTask) {
      // Calculate new task date and time
      const newDate = new Date(day);
      const newStartTime = `${hour.toString().padStart(2, '0')}:00:00`;
      
      // Calculate duration to maintain task length
      const originalStart = parseISO(`${draggedTask.shift_date}T${draggedTask.start_time}`);
      const originalEnd = parseISO(`${draggedTask.shift_date}T${draggedTask.end_time}`);
      const duration = (originalEnd - originalStart) / (1000 * 60 * 60); // hours
      
      const newEndHour = hour + Math.ceil(duration);
      const newEndTime = `${newEndHour.toString().padStart(2, '0')}:00:00`;
      
      // Update task
      const updatedTask = {
        ...draggedTask,
        shift_date: format(newDate, 'yyyy-MM-dd'),
        start_time: newStartTime,
        end_time: newEndTime
      };
      
      // Call parent update function
      if (handleTaskClick) {
        handleTaskClick(updatedTask);
      }
    }
    
    setDraggedTask(null);
  };

  // Handle day header click for loading templates in week view
  const handleDayHeaderClick = (day) => {
    if (!canEdit || !onLoadTemplate) return;
    
    onLoadTemplate(day);
  };

  // Week View
  if (viewMode === 'week') {
    return (
      <Box sx={{ 
        width: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: 8,
          height: 8
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: alpha(theme.palette.grey[300], 0.2)
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 4
        }
      }}>
        <Box sx={{ position: 'relative' }}>
          <CalendarGrid>
            {/* Time column header */}
            <Box />
            
            {/* Day headers */}
            {weekDays.map((day) => (
              <DayHeader 
                key={day.toString()} 
                istoday={isToday(day)}
                onClick={() => handleDayHeaderClick(day)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                  {format(day, 'EEE')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                  {format(day, 'd')}
                </Typography>
                {canEdit && onLoadTemplate && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                    Click to load template
                  </Typography>
                )}
              </DayHeader>
            ))}

            {/* Time slots and day columns */}
            {timeSlots.map((hour) => (
              <React.Fragment key={hour}>
                {/* Time label */}
                <TimeSlot>
                  {format(new Date().setHours(hour, 0, 0, 0), timeFormat === '24hour' ? 'HH:mm' : 'h a')}
                </TimeSlot>
                
                {/* Day columns for this hour */}
                {weekDays.map((day, dayIndex) => {
                  const hourTasks = currentTasks.filter(task => {
                    if (!task.shift_date || !isSameDay(parseISO(task.shift_date), day)) return false;
                    const startHour = getHours(parseISO(`${task.shift_date}T${task.start_time}`));
                    return startHour === hour;
                  });
                  
                  return (
                    <DayColumn 
                      key={`${day.toString()}-${hour}`}
                      sx={{
                        position: 'relative',
                        height: 50,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                        borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                        p: 0.5
                      }}
                    >
                      <Box sx={{ position: 'relative', height: '100%' }}>
                        <Box
                          onClick={() => handleSlotClick(day, hour)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            cursor: canEdit && handleTaskCreate ? 'pointer' : 'default',
                            '&:hover': canEdit && handleTaskCreate ? {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            } : {}
                          }}
                        />
                        
                        {hourTasks.length > 0 && (() => {
                          const maxVisible = 1;
                          const visibleTasks = hourTasks.slice(0, maxVisible);
                          const hiddenCount = hourTasks.length - maxVisible;

                          return (
                            <Box sx={{ position: 'relative', height: '100%', pointerEvents: 'none' }}>
                              {visibleTasks.map((task, index) => (
                                <Box
                                  key={task.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTaskClick(task);
                                  }}
                                  onContextMenu={(e) => handleContextMenu(e, task)}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, task)}
                                  sx={{
                                    position: 'absolute',
                                    top: index * 35,
                                    left: 0,
                                    right: hiddenCount > 0 ? 50 : 0,
                                    height: 32,
                                    backgroundColor: getDepartmentColor(task.department),
                                    color: 'white',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 1,
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    zIndex: 1,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    pointerEvents: 'auto'
                                  }}
                                >
                                  <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {task.employee?.first_name} {task.employee?.last_name}
                                  </Box>
                                </Box>
                              ))}
                              
                              {hiddenCount > 0 && (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const hourTasksForPopover = currentTasks.filter(task => 
                                      task.shift_date && isSameDay(parseISO(task.shift_date), day) &&
                                      getHours(parseISO(`${task.shift_date}T${task.start_time}`)) === parseInt(hour)
                                    );
                                    setSelectedDayShifts(hourTasksForPopover);
                                    setSelectedDate(day);
                                    setDayShiftsAnchor(e.currentTarget);
                                    setDayShiftsOpen(true);
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: (maxVisible - 1) * 35,
                                    right: 0,
                                    width: 45,
                                    height: 32,
                                    backgroundColor: alpha(theme.palette.grey[600], 0.8),
                                    color: 'white',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    zIndex: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'auto'
                                  }}
                                >
                                  +{hiddenCount}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                    </DayColumn>
                  );
                })}
              </React.Fragment>
            ))}
          </CalendarGrid>
        </Box>

        {/* Context Menu */}
        <Menu
          open={Boolean(contextMenu)}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined
          }
        >
          <MenuItem onClick={handleEditTask}>
            <EditIcon sx={{ mr: 1, fontSize: 16 }} />
            Edit Task
          </MenuItem>
          {handleTaskDelete && (
            <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Delete Task
            </MenuItem>
          )}
        </Menu>

        {/* Day Shifts Popover for Week View */}
        <Popover
          open={dayShiftsOpen}
          anchorEl={dayShiftsAnchor}
          onClose={handleDayShiftsClose}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              minWidth: 300,
              maxWidth: 400,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            
            <List sx={{ py: 0 }}>
              {selectedDayShifts.map((shift, index) => (
                <Box key={shift.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5)
                      }
                    }}
                    onClick={() => {
                      handleDayShiftsClose();
                      handleTaskClick(shift);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: getDepartmentColor(shift.department),
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      />
                      <ListItemText 
                        primary={`${shift.employee?.first_name} ${shift.employee?.last_name}`}
                        secondary={shift.start_time && shift.end_time ? `${format(parseISO(`${shift.shift_date}T${shift.start_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')} - ${format(parseISO(`${shift.shift_date}T${shift.end_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}` : ''}
                        primaryTypographyProps={{
                          sx: { fontSize: '0.9rem', fontWeight: 500 }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' }
                        }}
                      />
                    </Box>
                  </ListItem>
                  {index < selectedDayShifts.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </Box>
              ))}
            </List>
            
            {canEdit && handleTaskCreate && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleDayShiftsClose();
                  handleTaskCreate(selectedDate);
                }}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  fontWeight: 500,
                  color: 'primary.main',
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                Create Task
              </Button>
            )}
          </Box>
        </Popover>
      </Box>
    );
  }

  // Day View
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dayTasks = currentTasks.filter(task => 
    task.shift_date && isSameDay(parseISO(task.shift_date), currentDate)
  );

  return (
      <Box sx={{ p: 0, height: '100%', overflow: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', height: '100%' }} data-calendar-container>
          {/* Time column */}
          <Box sx={{ pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 60,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}
              >
                {format(new Date().setHours(hour, 0, 0, 0), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
              </Box>
            ))}
          </Box>
          
          {/* Shifts column */}
          <Box sx={{ position: 'relative', pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 60,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  cursor: canEdit && handleTaskCreate ? 'pointer' : 'default',
                  '&:hover': canEdit && handleTaskCreate ? {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  } : {}
                }}
                onClick={canEdit && handleTaskCreate ? () => handleSlotClick(currentDate, hour) : undefined}
              />
            ))}
            
            {/* Render tasks */}
            {(() => {
              const tasksByHour = {};
              dayTasks.forEach((task, originalIndex) => {
                const taskHour = getHours(new Date(task.shift_date + 'T' + task.start_time));
                if (!tasksByHour[taskHour]) tasksByHour[taskHour] = [];
                tasksByHour[taskHour].push({...task, originalIndex});
              });

              return Object.entries(tasksByHour)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([hour, hourTasks]) => {
                  hourTasks.sort((a, b) => a.originalIndex - b.originalIndex);
                  
                const top = (parseInt(hour) * 60) + 8;
                // Calculate actual available width based on the calendar container
                const containerElement = document.querySelector('[data-calendar-container]');
                let availableWidth;
                if (containerElement) {
                  const containerRect = containerElement.getBoundingClientRect();
                  availableWidth = Math.max(containerRect.width - 120, 400);
                } else {
                  availableWidth = Math.max(window.innerWidth - 200, 400);
                }
                
                let totalWidth = 0;
                let maxVisible = 0;
                
                // HARDCODE: Show up to 14 tasks, +X only if 15 or more
                if (hourTasks.length <= 13) {
                  maxVisible = hourTasks.length;
                } else {
                  maxVisible = 13;
                }
                
                // Always show at least one task
                if (maxVisible === 0 && hourTasks.length > 0) {
                  maxVisible = 1;
                }
                
                const visibleTasks = hourTasks.slice(0, maxVisible);
                const hiddenTasks = hourTasks.slice(maxVisible);
                const hiddenCount = hiddenTasks.length;

                return (
                  <Box key={hour} sx={{ position: 'absolute', top: `${top}px`, left: 8, right: 8, height: 50 }}>
                    {visibleTasks.map((task, index) => {
                      const taskTitle = `${task.employee?.first_name || ''} ${task.employee?.last_name || ''}`.trim();
                      const taskWidth = Math.min(Math.max(taskTitle.length * 7 + 40, 120), 250);
                      let leftPosition = 0;
                      
                      // Calculate position to avoid overlap
                      for (let i = 0; i < index; i++) {
                        const prevTaskTitle = `${visibleTasks[i].employee?.first_name || ''} ${visibleTasks[i].employee?.last_name || ''}`.trim();
                        const prevTaskWidth = Math.min(Math.max(prevTaskTitle.length * 7 + 40, 120), 250);
                        leftPosition += prevTaskWidth + 6;
                      }
                      
                      const departmentColor = getDepartmentColor(task.department);
                      
                      return (
                        <Box
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${leftPosition}px`,
                            width: `${taskWidth}px`,
                            height: 45,
                            backgroundColor: alpha(departmentColor, 0.9),
                            color: 'white',
                            borderRadius: 2,
                            p: 0.5,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                            boxShadow: `0 2px 4px ${alpha(departmentColor, 0.3)}`,
                            transition: 'all 0.2s ease',
                            pointerEvents: 'auto',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(departmentColor, 0.4)}`
                            }
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                            {taskTitle}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.7rem', 
                            opacity: 0.9, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1
                          }}>
                            {format(parseISO(`${task.shift_date}T${task.start_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')} - {format(parseISO(`${task.shift_date}T${task.end_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                          </Typography>
                        </Box>
                      );
                    })}
                    
                    {hiddenCount > 0 && (() => {
                      let buttonLeftPosition = 0;
                      for (let i = 0; i < visibleTasks.length; i++) {
                        const taskTitle = `${visibleTasks[i].employee?.first_name || ''} ${visibleTasks[i].employee?.last_name || ''}`.trim();
                        const taskWidth = Math.min(Math.max(taskTitle.length * 7 + 40, 120), 250);
                        buttonLeftPosition += taskWidth + (i > 0 ? 6 : 0);
                      }
                      if (visibleTasks.length > 0) {
                        buttonLeftPosition += 6;
                      }
                      
                      return (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDayShifts(hiddenTasks);
                            setSelectedDate(currentDate);
                            setDayShiftsAnchor(e.currentTarget);
                            setDayShiftsOpen(true);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${buttonLeftPosition}px`,
                            width: 60,
                            height: 45,
                            backgroundColor: alpha(theme.palette.grey[500], 0.9),
                            color: 'white',
                            borderRadius: 2,
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                            pointerEvents: 'auto'
                          }}
                        >
                          +{hiddenCount}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              });
            })()}
          </Box>
        </Box>
        
        {/* Day Shifts Popover for Day View */}
        <Popover
          open={dayShiftsOpen}
          anchorEl={dayShiftsAnchor}
          onClose={handleDayShiftsClose}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              minWidth: 300,
              maxWidth: 400,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            
            <List sx={{ py: 0 }}>
              {selectedDayShifts.map((shift, index) => (
                <Box key={shift.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5)
                      }
                    }}
                    onClick={() => {
                      handleDayShiftsClose();
                      handleTaskClick(shift);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: getDepartmentColor(shift.department),
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      />
                      <ListItemText 
                        primary={`${shift.employee?.first_name} ${shift.employee?.last_name}`}
                        secondary={shift.start_time && shift.end_time ? `${format(parseISO(`${shift.shift_date}T${shift.start_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')} - ${format(parseISO(`${shift.shift_date}T${shift.end_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}` : ''}
                        primaryTypographyProps={{
                          sx: { fontSize: '0.9rem', fontWeight: 500 }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' }
                        }}
                      />
                    </Box>
                  </ListItem>
                  {index < selectedDayShifts.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </Box>
              ))}
            </List>
            
            {canEdit && handleTaskCreate && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleDayShiftsClose();
                  handleTaskCreate(selectedDate);
                }}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  fontWeight: 500,
                  color: 'primary.main',
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                Create Task
              </Button>
            )}
          </Box>
        </Popover>
      </Box>
    );
};

export default ScheduleCalendar;